import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import {
    Autocomplete,
    Box,
    Button,
    Container,
    Grid,
    TextField,
    Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EventsSummaryCard from "./EventsSummaryCard";
import {
    process_,
    DATATYPE_CHECKIN,
    DATATYPE_CHECKIN_NAME,
    DATATYPE_RESDATE_NAME,
    PRESET_ANYTIME,
} from "../helpers/filters";
import { dayjsUTC, MIN_DATE, MAX_DATE } from "../helpers/dates";

const dayjs = dayjsUTC;

const EventPredictionAI = ({ selectedEvent, onBack }) => {
    const dataBookings = useSelector((state) => state.dataBookings);
    const dataSimple = useSelector((state) => state.dataSimple);
    const dataTargets = useSelector((state) => state.dataTargets);
    const js = useSelector((state) => state.globalReportFilters);
    const { id: hotelID } = useSelector((state) => state.hotelID);

    const pastEvents = useMemo(() => {
        if (!Array.isArray(dataSimple?.data?.Events?.data) || !selectedEvent)
            return [];
        const today = dayjs().startOf("day").valueOf();
        return dataSimple.data.Events.data
            .filter(
                (e) => e.ToDate < today && e.EventID !== selectedEvent.EventID,
            )
            .sort((a, b) => b.ToDate - a.ToDate);
    }, [dataSimple, selectedEvent]);

    const [comparisonEvent, setComparisonEvent] = useState(null);

    useEffect(() => {
        if (pastEvents.length === 0) {
            setComparisonEvent(null);
            return;
        }

        // TODO: implement an algorithm to find the most similar past event to the selected event
        setComparisonEvent((prev) => {
            if (prev && pastEvents.some((e) => e.EventID === prev.EventID))
                return prev;
            return pastEvents[0];
        });
    }, [pastEvents]);

    const buildEventJs = (event = {}) => {
        const isCheckin = js.dataType === DATATYPE_CHECKIN;

        const fromDay = event.FromDate
            ? dayjs(event.FromDate).format("YYYY-MM-DD")
            : isCheckin
              ? js.fromArrivalDay
              : js.fromDay;
        const toDay = event.ToDate
            ? dayjs(event.ToDate).format("YYYY-MM-DD")
            : isCheckin
              ? js.toArrivalDay
              : js.toDay;

        return {
            ...js,
            fromDay: isCheckin ? MIN_DATE.format("YYYY-MM-DD") : fromDay,
            toDay: isCheckin ? MAX_DATE.format("YYYY-MM-DD") : toDay,
            fromArrivalDay: isCheckin ? fromDay : MIN_DATE.format("YYYY-MM-DD"),
            toArrivalDay: isCheckin ? toDay : MAX_DATE.format("YYYY-MM-DD"),
            presetArrivalDate: PRESET_ANYTIME,
            fromResDateDay: MIN_DATE.format("YYYY-MM-DD"),
            toResDateDay: MAX_DATE.format("YYYY-MM-DD"),
            groupBy: [],
        };
    };

    const selectedMetrics = useMemo(() => {
        if (!selectedEvent) return null;
        const result = process_({
            js: buildEventJs({}), // buildEventJS(selectedEvent), to always use the event dates, not the ones carried over(global js)
            dataBookings,
            hotelID,
            dataSimple,
            dataTargets,
        });
        return Object.values(result.entries)[0]?.result ?? null;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedEvent, dataBookings, dataSimple, dataTargets, hotelID, js]);

    const comparisonMetrics = useMemo(() => {
        if (!comparisonEvent) return null;
        const result = process_({
            js: buildEventJs(comparisonEvent),
            dataBookings,
            hotelID,
            dataSimple,
            dataTargets,
        });
        return Object.values(result.entries)[0]?.result ?? null;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [comparisonEvent, dataBookings, dataSimple, dataTargets, hotelID, js]);

    return (
        <Container maxWidth={false}>
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    mt: 2,
                    mb: 2,
                }}
            >
                <Typography variant="h4">Event Prediction - AI</Typography>

                <Button
                    startIcon={<ArrowBackIcon />}
                    onClick={onBack}
                    sx={{ mb: 1 }}
                >
                    Back to Events Performance
                </Button>
            </Box>

            <Typography variant="subtitle1" color="initial">
                AI-suggested approach for{" "}
                <strong>{selectedEvent?.Name ?? "selected event"}</strong> based
                on past event performance{" "}
                <strong>
                    (
                    {js.dataType === DATATYPE_CHECKIN
                        ? DATATYPE_CHECKIN_NAME
                        : DATATYPE_RESDATE_NAME}
                    )
                </strong>
                .
            </Typography>

            <Grid container spacing={2} sx={{ mt: 2 }}>
                <Grid item xs={12} md={6}>
                    <EventsSummaryCard
                        mode="performance"
                        title="Previous Event Performance"
                        event={comparisonEvent ?? selectedEvent}
                        metrics={comparisonMetrics}
                        headerExtra={
                            <Autocomplete
                                size="small"
                                options={pastEvents}
                                renderOption={(props, option) => (
                                    <li {...props} key={option.EventID}>
                                        {option.Name}
                                    </li>
                                )}
                                getOptionLabel={(o) => o.Name}
                                noOptionsText={"No historical events available"}
                                value={comparisonEvent}
                                onChange={(_, v) =>
                                    setComparisonEvent(
                                        v ?? pastEvents[0] ?? null,
                                    )
                                }
                                disableClearable
                                sx={{ width: 200 }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        placeholder={
                                            pastEvents.length === 0
                                                ? "No historical events available"
                                                : "Select an event"
                                        }
                                        variant="outlined"
                                    />
                                )}
                            />
                        }
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <EventsSummaryCard
                        mode="performance"
                        title="Suggested Approach"
                        event={selectedEvent}
                        metrics={selectedMetrics}
                    />
                </Grid>
            </Grid>
        </Container>
    );
};

export default EventPredictionAI;
